import ArticleLayout from '../../components/ArticleLayout';

export default function RadiatorReturventil() {
  return (
    <ArticleLayout
      title="Returventil og forindstilling: Hvorfor du ikke bare må skrue op"
      metaTitle="Radiator returventil og forindstilling: Sådan virker det"
      metaDescription="Lær hvad forindstilling og returventiler på radiatoren gør for den hydrauliske balance. Se hvorfor maksimal åbning giver dårlig varmeøkonomi."
      canonicalPath="/radiator-returventil"
      introLead="En radiatorstyring består ikke kun af den synlige termostat på toppen, men også af en intern forindstilling i ventilen og ofte en afspærringsventil på returrøret i bunden."
      introAnswer="Forindstillingen begrænser den maksimale vandmængde (flow), der kan passere gennem den enkelte radiator. Uden korrekt forindstilling vil den radiator, der sidder tættest på pumpen, sluge alt det varme vand, mens radiatorer på overetagen eller i husets fjerneste hjørne forbliver lunkne og underforsynede."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Kontrol af ventiler og forindstilling' },
        { id: 'hvad-er-forindstilling', title: '2. Hvad er forindstilling og hydraulisk balance?' },
        { id: 'returforskruning', title: '3. Returforskruningen i bunden af radiatoren' },
        { id: 'justering-regler', title: '4. Hvorfor må man ikke bare sætte alt på maksimum?' },
        { id: 'faq', title: '5. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: Sådan tjekker du forindstilling og returventil"
      checklist={[
        {
          title: 'Tjek om returventilen i bunden er åben',
          description: 'Skru dækhætten af returforskruningen i bunden med fingrene for at komme ind til justerskruen.',
        },
        {
          title: 'Kontrollér at den indvendige unbrakoskrue ikke er skruet i bund',
          description: 'En lukket returskrue bruges til at afspærre radiatoren under malerarbejde eller udskiftning.',
        },
        {
          title: 'Afmonter termostathovedet for at se ventilens forindstillingsring',
          description: 'På Danfoss RA-ventiler ses en lille drejelig skala med tal fra 1 til 7 samt mærket N.',
        },
        {
          title: 'Undersøg om forindstillingen matcher radiatorens størrelse',
          description: 'En lille radiator i et gæstetoilet bør stå lavt (fx 2 eller 3), mens en stor stueradiator står højere.',
        },
        {
          title: 'Overlad den samlede indregulering til en fagmand med beregninger',
          description: 'Korrekt hydraulisk balance kræver beregning af husets varmetab og vandmængder.',
        },
      ]}
      sections={[
        {
          id: 'hvad-er-forindstilling',
          title: 'Hvad er forindstilling og hvorfor er den afgørende?',
          content: (
            <>
              <p>
                I et centralvarmeanlæg tager vandet altid den nemmeste vej – dvs. vejen med mindst modstand.
              </p>
              <p>
                Hvis alle ventiler i huset er fuldstændig åbne indvendigt, vil vandet suse med høj hastighed igennem de første radiatorer tættest på varmekilden eller fjernvarmeuniten. Disse radiatorer når knap at afkøle vandet, før det fosser retur. Samtidig opstår der et trykfald, som betyder, at radiatorerne på 1. sal eller i den modsatte ende af huset næsten ikke modtager noget varmt vand overhovedet.
              </p>
              <p>
                Med forindstilling (integreret i Danfoss-ventilhuset bag termostaten) indsnævrer man åbningen præcist til den vandmængde, den pågældende radiator skal bruge. Det kaldes for <em>hydraulisk balance</em>.
              </p>
            </>
          ),
        },
        {
          id: 'returforskruning',
          title: 'Returforskruningen: Den lille ventil i radiatorens bund',
          content: (
            <>
              <p>
                Nederst på radiatoren – dér hvor det afkølede vand løber ud i røret – sidder ofte en returforskruning. Den er dækket af en lille hætte af metal eller plast.
              </p>
              <p>
                Bag hætten sidder en unbrakoskrue eller en ligekærvskrue. Dens primære formål er at fungere som serviceventil: Hvis radiatoren skal tages ned for maling af væggen eller udskiftning, lukkes termostaten i toppen og returskruen spændes i bund med en unbrakonøgle. Derved er radiatoren isoleret fra resten af anlægget, så kun selve radiatorens vand skal aftappes.
              </p>
            </>
          ),
        },
        {
          id: 'justering-regler',
          title: 'Hvorfor må du ikke bare sætte skalaen på N (maksimum)?',
          content: (
            <>
              <p>
                På Danfoss RA-ventiler angiver bogstavet <span className="font-mono font-bold">N</span> fabrikkens standardindstilling med fuld, ubremset gennemstrømning (bruges kun til gennemskylning af anlægget).
              </p>
              <p>
                Hvis man stiller alle radiatorer på <span className="font-mono">N</span>, opnår man to alvorlige problemer:
              </p>
              <p>
                1. <strong>Uens varmefordeling:</strong> De bagerste værelser i huset bliver kolde i frostvejr, uanset hvor højt der skrues op for termostaten.
              </p>
              <p>
                2. <strong>Høje returtemperaturer på fjernvarme:</strong> Vandet passerer for hurtigt igennem uden at afkøles ordentligt. Det koster strafgebyrer til fjernvarmeselskabet for dårlig afkøling.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Hvornår skal VVS-fagmanden foretage en indregulering?',
        content: (
          <p>
            Hvis husets radiatorer varmer meget ujævnt – for eksempel hvis overetagen altid fryser mens stueetagen koger – bør du få en VVS-installatør til at udføre en fuld hydraulisk indregulering af anlægget. Fagmanden beregner rumstørrelser og indstiller tallene på hver enkelt ventil præcist efter tabeller.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Hvad betyder tallene 1 til 7 på ventilens forindstillingsring?',
          a: 'Tallene angiver ventilens indre lysning (gennemstrømningsmodstand). Trin 1 giver det mindste vandflow (til helt små radiatorer), mens trin 7 tillader et stort flow til store radiatorer i store rum.',
        },
        {
          q: 'Kan man selv ændre forindstillingen?',
          a: 'Ja, den justeres ved at løfte den fjedrende ring ud og dreje den til det ønskede tal ud for referencemærket. Man bør dog altid notere den oprindelige fabriksindstilling ned først.',
        },
        {
          q: 'Skal returventilen i bunden altid stå helt åben?',
          a: 'Ja, medmindre den specifikt anvendes som forindstillingsventil (hvilket ses på ældre anlæg uden forindstilling i termostatventilen). I moderne anlæg skal den normalt være skruet helt ud i åben position under normal drift.',
        },
      ]}
      internalLinks={[
        { to: '/radiator-varmer-ikke', text: 'Radiatoren varmer ikke', desc: 'overblik over fejlkilder' },
        { to: '/radiator-kold-i-bunden', text: 'Radiator kold i bunden', desc: 'forstå afkøling og temperaturfald' },
        { to: '/fjernvarme-radiator-virker-ikke', text: 'Fjernvarme og radiatorer', desc: 'vigtigheden af lav returtemperatur' },
        { to: '/radiator-termostat', text: 'Radiatortermostater forklaret', desc: 'samspillet mellem føler og ventil' },
      ]}
      sources={[
        { url: 'https://www.danfoss.com/da-dk/products/dhs/radiator-and-room-thermostats/radiator-thermostats/radiator-valves/', label: 'Radiatorventiler og termostatiske radiatorventiler | Danfoss' },
        { url: 'https://kredslob.dk/privat/fjernvarme/returtemperatur', label: 'Få styr på returtemperaturen og spar på varmeregningen Kredsløb' },
      ]}
    />
  );
}
